import { useNavigate, useParams } from "react-router-dom";
import { useBrand } from "../hooks/useBrand";
import { useBrandDocsList, type BrandDoc } from "../hooks/useBrandDocsList";
import { WorkspaceContentFrame } from "../components/workspace/WorkspaceContentFrame";
import { AgentUIComposer } from "../components/agent-v2/AgentUIComposer";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { useWorkspaceTabs } from "../contexts/WorkspaceTabsContext";

export function BrandDocsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { openTab } = useWorkspaceTabs();
  const { data: brandDetail, isLoading: brandLoading, isError: brandError } = useBrand(brandId);
  // The contextual docs list lives in the shell-level ShellSidePanel now; the page
  // shares the same source so the panel and the main library never drift.
  const { docs, docsReadyCount, docsLoading } = useBrandDocsList(brandId);

  const brand = brandDetail?.data ?? null;

  function openDoc(doc: BrandDoc) {
    if (!brandId || !doc.href) return;
    openTab({ id: `artifact:${doc.id}:${brandId}`, kind: "artifact", label: doc.title, route: doc.href });
    navigate(doc.href);
  }

  if (!brandId) {
    return (
      <div className="py-12 text-center">
        <h2 className="mb-2 text-xl font-bold text-slate-900">Brand Docs needs a brand</h2>
        <p className="text-sm text-slate-500">Choose a brand before opening canonical docs.</p>
      </div>
    );
  }

  if (brandLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (brandError || !brand) {
    return (
      <div className="mx-auto max-w-3xl py-12 text-center">
        <h2 className="text-2xl font-semibold text-slate-900">This brand could not be opened</h2>
        <p className="mt-3 text-sm leading-6 text-slate-500">
          The brand may have been removed, the link may be stale, or you may no longer have access.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="secondary" onClick={() => navigate("/brand")}>Choose brand</Button>
          <Button onClick={() => navigate("/home")}>Back to workspace</Button>
        </div>
      </div>
    );
  }

  return (
    <WorkspaceContentFrame>
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col px-6 py-8">
        <header className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Brand Docs</p>
            <h1 className="mt-1 text-[32px] font-bold tracking-tight text-slate-900">{brand.name} canonical docs</h1>
            <p className="mt-2 max-w-2xl text-[15px] leading-7 text-slate-500">
              Strategy and voice are durable operator docs. Open each one as an active work object instead of treating them like settings forms.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">{docsReadyCount} canonical docs</Badge>
            {docsLoading ? <Badge>Syncing</Badge> : null}
          </div>
        </header>

        <div className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Canonical library</p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">Durable docs</h2>
            </div>
            <div className="space-y-4">
              {docs.map((doc) => (
                <div key={doc.id} className="rounded-2xl border border-slate-200 bg-white px-5 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="mb-2 flex items-center gap-2">
                        <Badge variant={doc.ready ? "info" : "default"}>{doc.status}</Badge>
                        {doc.revision ? <span className="font-mono text-xs text-slate-400">v{doc.revision}</span> : null}
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900">{doc.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-500">{doc.summary}</p>
                    </div>
                    {doc.href ? (
                      <Button variant="secondary" onClick={() => openDoc(doc)}>Open</Button>
                    ) : null}
                  </div>
                  {doc.error ? (
                    <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      This doc could not be fully loaded right now.
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Operator guidance</p>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">How to use these docs</h2>
            </div>
            <div className="space-y-4 text-sm leading-6 text-slate-600">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                Open strategy when you need to change positioning, offer framing, or audience assumptions.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                Open voice when you need to update tone, examples, or learned corrections from review loops.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                Use chat for intent and iteration, but keep accepted brand knowledge in canonical docs.
              </div>
            </div>
          </section>
        </div>

        <div className="border-t border-transparent pt-4">
          <AgentUIComposer
            onSend={(content) => navigate(`/agent/${brandId}`, { state: { draft: content } })}
            onStop={() => {}}
            isStreaming={false}
            disabled={false}
            initialValue=""
            contextLabel="Ask HelloConvo to review or revise brand docs"
          />
        </div>
      </div>
    </WorkspaceContentFrame>
  );
}
